import { Button, Card, Grid, LoadingOverlay, Select, Stack, Text, Title, Textarea, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy } from "@tabler/icons-react";
import { collection, getDocs } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { firestore } from "@/base";
import { themeTokens } from "@/src/theme/tokens";
import { useProcessarPlanilha } from "./useProcessarPlanilha";

interface ConferenciaData {
  empresa: any;
  totalRespostas: number;
  totalPerguntas: number;
  totalGrupos: number;
  totalMedias: number;
  mediaAtual: number;
  mediaAtualFonte: string;
  mediaAA: string;
  mediaAvaliadores: string;
  totalizadoresPorGrupo: any[];
  respostas: any[];
  dadosMedias?: any[];
  dadosRespostas?: any[];
}

const ConferenciaPage = () => {
  const [licenciadas, setLicenciadas] = useState<any[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [questionarios, setQuestionarios] = useState<any[]>([]);

  const [selectedLicenciada, setSelectedLicenciada] = useState<string>("");
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>("");
  const [selectedEmpresaDesejada, setSelectedEmpresaDesejada] = useState<string>("");
  const [selectedQuestionario, setSelectedQuestionario] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [resultado, setResultado] = useState<ConferenciaData | null>(null);

  const { processarPlanilhaEntrevista } = useProcessarPlanilha();
  const licenciadaOptions = useMemo(
    () =>
      licenciadas.map((licenciada) => ({
        value: licenciada.id,
        label: licenciada.razaoSocial || licenciada.nome || licenciada.id
      })),
    [licenciadas]
  );
  const empresaOptions = useMemo(
    () =>
      empresas.map((empresa) => ({
        value: empresa.id,
        label: empresa.nomeFantasia || empresa.razaoSocial || empresa.id
      })),
    [empresas]
  );
  const questionarioOptions = useMemo(
    () =>
      questionarios.map((questionario) => ({
        value: questionario.id,
        label: questionario.nome || questionario.titulo || questionario.id
      })),
    [questionarios]
  );

  const handleLicenciadaChange = useCallback((value: string | null) => {
    setSelectedLicenciada(value || "");
  }, []);

  const handleEmpresaChange = useCallback((value: string | null) => {
    setSelectedEmpresa(value || "");
  }, []);

  const handleEmpresaDesejadaChange = useCallback((value: string | null) => {
    setSelectedEmpresaDesejada(value || "");
  }, []);

  const handleQuestionarioChange = useCallback((value: string | null) => {
    setSelectedQuestionario(value || "");
  }, []);

  // Buscar licenciadas
  useEffect(() => {
    const fetchLicenciadas = async () => {
      try {
        const snapshot = await getDocs(collection(firestore, "licenciadas"));
        const data = snapshot.docs.map((document_) => ({
          id: document_.id,
          ...document_.data()
        }));
        console.log(data.length, "licenciadas carregadas");
        setLicenciadas(data);
      } catch (error) {
        console.error("Erro ao buscar licenciadas:", error);
      }
    };
    fetchLicenciadas();
  }, []);

  // Buscar empresas quando licenciada é selecionada
  useEffect(() => {
    if (!selectedLicenciada) {
      setEmpresas([]);
      setSelectedEmpresa("");
      setSelectedEmpresaDesejada("");
      return;
    }

    const fetchEmpresas = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(firestore, `licenciadas/${selectedLicenciada}/empresas`));
        const data = snapshot.docs.map((document_) => ({
          id: document_.id,
          ...document_.data()
        }));
        setEmpresas(data);
      } catch (error) {
        console.error("Erro ao buscar empresas:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmpresas();
  }, [selectedLicenciada]);

  // Buscar questionários quando empresa é selecionada
  useEffect(() => {
    if (!selectedLicenciada || !selectedEmpresa) {
      setQuestionarios([]);
      setSelectedQuestionario("");
      return;
    }

    const fetchQuestionarios = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(
          collection(firestore, `licenciadas/${selectedLicenciada}/empresas/${selectedEmpresa}/questionarios`)
        );
        const data = snapshot.docs.map((document_) => ({
          id: document_.id,
          ...document_.data()
        }));
        setQuestionarios(data);
      } catch (error) {
        console.error("Erro ao buscar questionários:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionarios();
  }, [selectedLicenciada, selectedEmpresa]);

  const processarPlanilha = async () => {
    if (!selectedLicenciada || !selectedEmpresa) {
      alert("Selecione licenciada e empresa");
      return;
    }

    setProcessing(true);
    setResultado(null);

    try {
      const result = await processarPlanilhaEntrevista({
        licenciadaId: selectedLicenciada,
        empresaId: selectedEmpresa,
        empresaDesejadaId: selectedEmpresaDesejada || undefined,
        questionarioId: selectedQuestionario || undefined
      });

      setResultado(result);
    } catch (error) {
      console.error("Erro ao processar planilha:", error);
      alert(`Erro ao processar dados: ${error}`);
    } finally {
      setProcessing(false);
    }
  };

  const formatarDadosParaTexto = (dados: any[], tipo: "medias" | "respostas") => {
    if (!dados || dados.length === 0) return "";

    const cabecalho =
      tipo === "medias"
        ? '"Empresa","Respondente","Grupo","Pergunta","Nota","Peso Calculado"\n'
        : '"Empresa","Respondente","Grupo","Pergunta","Valor","Peso Calculado"\n';

    const linhas = dados
      .map((item) => {
        const valor = tipo === "medias" ? item.nota : item.valor;
        return `"${item.empresa}","${item.respondente}","${item.grupo}","${item.pergunta}","${valor}","${item.pesoCalculado}"`;
      })
      .join("\n");

    return cabecalho + linhas;
  };

  const copiarParaClipboard = async (texto: string, label: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      notifications.show({
        title: "Copiado!",
        message: `Dados de ${label} copiados para o clipboard`,
        color: "green"
      });
    } catch {
      notifications.show({
        title: "Erro",
        message: "Não foi possível copiar os dados",
        color: "red"
      });
    }
  };

  const handleCopyMedias = useCallback(() => {
    if (!resultado?.dadosMedias) {
      return;
    }
    copiarParaClipboard(formatarDadosParaTexto(resultado.dadosMedias, "medias"), "Medias");
  }, [resultado?.dadosMedias]);

  const handleCopyRespostas = useCallback(() => {
    if (!resultado?.dadosRespostas) {
      return;
    }
    copiarParaClipboard(formatarDadosParaTexto(resultado.dadosRespostas, "respostas"), "Respostas");
  }, [resultado?.dadosRespostas]);

  return (
    <Stack gap="lg">
      <Title order={2}>Conferência - Processamento de Planilha de Entrevista</Title>

      <Card padding="lg" radius="md" withBorder>
        <LoadingOverlay visible={loading} />
        <Stack gap="md">
          <Text fw={500}>Seleção de Dados</Text>

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Licenciada"
                placeholder="Selecione uma licenciada"
                data={licenciadaOptions}
                value={selectedLicenciada}
                onChange={handleLicenciadaChange}
                searchable
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Empresa (Respostas)"
                placeholder="Selecione uma empresa"
                data={empresaOptions}
                value={selectedEmpresa}
                onChange={handleEmpresaChange}
                disabled={!selectedLicenciada}
                searchable
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Empresa Desejada (Filtro)"
                placeholder="Selecione uma empresa para filtrar"
                data={empresaOptions}
                value={selectedEmpresaDesejada}
                onChange={handleEmpresaDesejadaChange}
                disabled={!selectedLicenciada}
                searchable
                clearable
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Questionário (Filtro)"
                placeholder="Selecione um questionário"
                data={questionarioOptions}
                value={selectedQuestionario}
                onChange={handleQuestionarioChange}
                disabled={!selectedEmpresa}
                searchable
                clearable
              />
            </Grid.Col>
          </Grid>

          <Button
            onClick={processarPlanilha}
            loading={processing}
            disabled={!selectedLicenciada || !selectedEmpresa}
            size="md"
          >
            Processar Planilha
          </Button>
        </Stack>
      </Card>

      {resultado && (
        <Stack gap="lg">
          <Card padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={500} size="lg">
                Resumo Geral
              </Text>
              <Grid>
                <Grid.Col span={3}>
                  <Text>
                    Total de Respostas: <strong>{resultado.totalRespostas}</strong>
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text>
                    Total de Perguntas: <strong>{resultado.totalPerguntas}</strong>
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text>
                    Total de Grupos: <strong>{resultado.totalGrupos}</strong>
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text>
                    Total de Médias: <strong>{resultado.totalMedias}</strong>
                  </Text>
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={6}>
                  <Text>
                    Média Auto Avaliação: <strong style={{ color: "#228be6" }}>{resultado.mediaAA}</strong>
                  </Text>
                  <Text>
                    Média Avaliadores: <strong style={{ color: "#228be6" }}>{resultado.mediaAvaliadores}</strong>
                  </Text>
                </Grid.Col>
              </Grid>
              {resultado.empresa && (
                <Text>
                  Empresa: <strong>{resultado.empresa.empresaNome}</strong>
                </Text>
              )}
            </Stack>
          </Card>

          {/* Dados da Collection Medias */}
          {resultado.dadosMedias && resultado.dadosMedias.length > 0 && (
            <Card padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Text fw={500} size="lg">
                    Dados da Collection Medias ({resultado.dadosMedias.length} registros)
                  </Text>
                  <Button leftSection={<IconCopy size={16} />} variant="light" size="sm" onClick={handleCopyMedias}>
                    Copiar CSV
                  </Button>
                </Group>
                <Textarea
                  value={formatarDadosParaTexto(resultado.dadosMedias, "medias")}
                  readOnly
                  autosize
                  minRows={10}
                  maxRows={20}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "12px",
                      backgroundColor: themeTokens.colors.surface
                    }
                  }}
                />
              </Stack>
            </Card>
          )}

          {/* Dados da Collection Respostas */}
          {resultado.dadosRespostas && resultado.dadosRespostas.length > 0 && (
            <Card padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Text fw={500} size="lg">
                    Dados da Collection Respostas ({resultado.dadosRespostas.length} registros)
                  </Text>
                  <Button leftSection={<IconCopy size={16} />} variant="light" size="sm" onClick={handleCopyRespostas}>
                    Copiar CSV
                  </Button>
                </Group>
                <Textarea
                  value={formatarDadosParaTexto(resultado.dadosRespostas, "respostas")}
                  readOnly
                  autosize
                  minRows={10}
                  maxRows={20}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "12px",
                      backgroundColor: "#fff5f5"
                    }
                  }}
                />
              </Stack>
            </Card>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ConferenciaPage;
